import { useEffect, useRef, useState } from "react";

interface Props { value: number; suffix?: string; duration?: number; prefix?: string }

/**
 * Render slutvärdet redan vid SSR/initial render (FEN-002) så crawlers
 * och no-JS-användare aldrig ser "0". När elementet skrollas in
 * animeras siffran från 0 → value som visuell effekt.
 */
export function LiveCountUp({ value, suffix = "", prefix = "", duration = 1600 }: Props) {
  // Starta på slutvärdet → hydration matchar SSR och inga nollor flashas.
  const [n, setN] = useState<number>(value);
  const ref = useRef<HTMLSpanElement | null>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return; // respektera reduced motion – behåll slutvärdet
    }
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          setN(0);
          const start = performance.now();
          const tick = (t: number) => {
            const p = Math.min(1, (t - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setN(Math.round(value * eased));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          io.disconnect();
        }
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [value, duration]);

  return <span ref={ref}>{prefix}{n.toLocaleString("sv-SE")}{suffix}</span>;
}
